import React, { forwardRef, useCallback, useContext, useImperativeHandle, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useQuickCreate, useQuickCreateDialogActions } from '@/hooks/use-quick-create';
import { SearchSuggestionsDropdown } from './components/search-suggestions-dropdown';
import { getDisplayText, hasSelectionCheckboxColumn, resolveRowId } from './helpers';
import { AritoFormV2Context } from '@/components/custom/erp/form-v2/context';
import { useMultipleSearchValue } from './hooks/use-multiple-search-value';
import { SearchInputControl } from './components/search-input-control';
import { useSearchDialogState } from './hooks/use-search-dialog-state';
import { useSearchSuggestions } from './hooks/use-search-suggestions';
import { QUICK_CREATE_CONFIGS } from '@/configs/quick-create-config';
import { getPermissionResourceKeyFromPath } from '@/lib/permissions';
import type { SearchFieldProps, SearchFieldRef } from './types';
import { useSearchRequest } from './hooks/use-search-request';
import { getFieldErrorMessage } from '../field-validation';
import { SearchDialog } from './components/search-dialog';
import { QUERY_KEYS } from '@/constants/query-keys';
import { useAuth } from '@/contexts/auth-context';
import { cn } from '@/lib/utils';

export type { SearchFieldRef } from './types';

const disabledValueClassName =
  'flex h-[var(--erp-control-height)] w-full min-w-0 items-center overflow-hidden text-ellipsis whitespace-nowrap px-2 text-[13px] font-semibold text-black';

const errorTooltipClassName = 'z-[99999] border-none bg-[#333] px-2 py-1 text-xs text-white';

const SearchFieldInner = <T,>(
  {
    className,
    classNameRelatedField,
    columnDisplay,
    dialogBaseZIndex,
    dialogTitle = 'Danh mục',
    dialogPageSize,
    disabled = false,
    dialogActions,
    error,
    errorDisplay = 'message',
    displayRelatedField,
    headerFields,
    hideRowNumberColumn = false,
    refreshKey,
    multiple = false,
    name,
    onBeforeOpen,
    onRowSelection,
    onValueChange,
    otherSearchEndpoint,
    placeholder,
    quickCreate,
    relatedFieldValue,
    rows,
    searchColumns = [],
    searchEndpoint,
    separator = ',',
    suggestionPageSize,
    type = 'text',
    value
  }: SearchFieldProps<T>,
  ref: React.ForwardedRef<SearchFieldRef<T>>
) => {
  const aritoFormContext = useContext(AritoFormV2Context);
  const hookFormContext = useFormContext();
  const { permissions, isAdmin } = useAuth();
  const formValue = name ? hookFormContext?.watch(name) : undefined;
  const resolvedValue = value !== undefined ? value : formValue;
  const fieldError =
    error ||
    getFieldErrorMessage(aritoFormContext?.errors, name) ||
    getFieldErrorMessage(hookFormContext?.formState?.errors, name);
  const invalid = Boolean(fieldError);
  const shouldRenderErrorMessage = Boolean(fieldError) && errorDisplay === 'message';
  const shouldRenderErrorTooltip = Boolean(fieldError) && errorDisplay === 'tooltip';
  const withErrorTooltip = (children: React.ReactElement) => {
    if (!shouldRenderErrorTooltip) {
      return children;
    }

    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{children}</TooltipTrigger>
          <TooltipContent side='top' align='start' className={errorTooltipClassName}>
            {fieldError}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };
  const commitFormValue = useCallback(
    (nextValue: any) => {
      if (name && hookFormContext) {
        hookFormContext.setValue(name, nextValue, {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: true
        });
      }

      onValueChange?.(nextValue);
    },
    [hookFormContext, name, onValueChange]
  );
  const triggerValidation = useCallback(() => {
    if (name && hookFormContext) {
      void hookFormContext.trigger(name);
    }
  }, [hookFormContext, name]);
  const searchRequest = useSearchRequest<T>(searchEndpoint, otherSearchEndpoint);
  const quickCreateProvider = useQuickCreate();

  const resolvedQuickCreate = useMemo(() => {
    if (quickCreate) return quickCreate;

    if (!searchEndpoint) return undefined;

    const fullKey = searchEndpoint.replace(/^\/+|\/+$/g, '');
    const cleanKey = (str: string) => str.split('?')[0].replace(/^\/+|\/+$/g, '');
    const endpointKey = cleanKey(searchEndpoint);

    // Find if we have a match in QUERY_KEYS (ignoring query params)
    const originalQueryKey = Object.values(QUERY_KEYS).find(
      val => typeof val === 'string' && cleanKey(val) === endpointKey
    );

    // Use the original query key (with query params) as the adapter key
    const finalKey = originalQueryKey ? originalQueryKey.replace(/^\/+|\/+$/g, '') : endpointKey;

    const hasAdapter =
      QUICK_CREATE_CONFIGS[fullKey] ||
      QUICK_CREATE_CONFIGS[endpointKey] ||
      (originalQueryKey &&
        typeof originalQueryKey === 'string' &&
        originalQueryKey.includes('/') &&
        originalQueryKey.startsWith('danh-muc/'));

    if (hasAdapter) {
      const adapterKey = QUICK_CREATE_CONFIGS[fullKey] ? fullKey : finalKey;
      const resourceKey = getPermissionResourceKeyFromPath(adapterKey);
      const hasCreatePermission = isAdmin || (resourceKey && permissions?.[resourceKey]?.create === true);

      if (!hasCreatePermission) {
        return undefined;
      }

      return {
        key: adapterKey,
        allowEdit: true
      };
    }

    return undefined;
  }, [quickCreate, searchEndpoint, permissions, isAdmin]);

  const { closeDialog, isFullScreen, isMobile, open, openDialog, setOpen, toggleFullScreen } = useSearchDialogState();
  const [dialogSelectedObj, setDialogSelectedObj] = useState<T | null>(null);
  const [dialogSelectedItems, setDialogSelectedItems] = useState<T[]>([]);
  const supportsDialogMultiSelect = multiple && hasSelectionCheckboxColumn(searchColumns);

  const multipleSearch = useMultipleSearchValue<T>({
    columnDisplay,
    onRowSelection: multiple ? (onRowSelection as ((rows: T[]) => void) | undefined) : undefined,
    onValueChange: commitFormValue,
    pageSize: dialogPageSize,
    rows,
    searchRequest,
    separator,
    value: resolvedValue ?? ''
  });

  const {
    canGoNext,
    canGoPrevious,
    commitSelection,
    dropdownPositionStyle,
    getColumnDisplayValue,
    getColumnStyle,
    goToNextPage,
    goToPreviousPage,
    handleInputBlur,
    handleInputChange,
    handleInputFocus,
    handleInputKeyDown,
    highlightedIndex,
    inputValue,
    isLoading,
    selectedObj,
    setHighlightedIndex,
    shouldShowDropdown,
    showNoResults,
    showPagination,
    suggestionColumns,
    suggestionContainerRef,
    suggestionDropdownRef,
    suggestions
  } = useSearchSuggestions<T>({
    columnDisplay,
    displayRelatedField,
    onRowSelection: row => {
      if (multiple) {
        multipleSearch.appendSelection(row);
        return;
      }

      (onRowSelection as ((row: T | null) => void) | undefined)?.(row);
    },
    onValueChange: multiple ? undefined : commitFormValue,
    pageSize: suggestionPageSize,
    rows,
    searchColumns,
    searchRequest,
    value: multiple ? multipleSearch.inputValue : (resolvedValue ?? '')
  });

  const quickCreateActions = useQuickCreateDialogActions<T>({
    allowEdit: resolvedQuickCreate?.allowEdit,
    closeLookupOnOpen: resolvedQuickCreate?.closeLookupOnOpen,
    context: resolvedQuickCreate?.context,
    createIcon: resolvedQuickCreate?.createIcon,
    createTitle: resolvedQuickCreate?.createTitle,
    editIcon: resolvedQuickCreate?.editIcon,
    editTitle: resolvedQuickCreate?.editTitle,
    initialData: !multiple ? selectedObj : null,
    key: resolvedQuickCreate?.key ?? '',
    onSaved: resolvedQuickCreate?.onSaved
  });
  const resolvedDialogActions = useMemo(
    () => [...(dialogActions ?? []), ...(resolvedQuickCreate ? quickCreateActions : [])],
    [dialogActions, resolvedQuickCreate, quickCreateActions]
  );
  const resolvedRefreshKey = resolvedQuickCreate
    ? `${refreshKey ?? ''}:${quickCreateProvider?.getRefreshKey(resolvedQuickCreate.key) ?? 0}`
    : refreshKey;

  useImperativeHandle(
    ref,
    () => ({
      clearError: () => multipleSearch.setError(null),
      getError: () => (multiple ? multipleSearch.error : null),
      getInputValue: () => (multiple ? multipleSearch.inputValue : inputValue),
      getSelectedItems: () => (multiple ? multipleSearch.selectedItems : selectedObj ? [selectedObj] : []),
      isResolving: () => (multiple ? multipleSearch.isSearching : isLoading),
      resolveCurrentInput: () =>
        multiple
          ? multipleSearch.resolveCurrentInput()
          : Promise.resolve({ items: selectedObj ? [selectedObj] : [], notFoundCodes: [] })
    }),
    [inputValue, isLoading, multiple, multipleSearch, selectedObj]
  );

  const handleMultipleSuggestionSelection = useCallback(
    (row: T) => {
      multipleSearch.appendSelection(row);
    },
    [multipleSearch]
  );

  const openSearchDialog = useCallback(async () => {
    try {
      await onBeforeOpen?.();
    } catch {
      // Ignore pre-open errors so the search dialog still opens.
    }

    setDialogSelectedObj(multiple ? null : selectedObj);
    setDialogSelectedItems(multiple ? multipleSearch.selectedItems : []);
    openDialog();
  }, [multiple, multipleSearch.selectedItems, onBeforeOpen, openDialog, selectedObj]);

  const handleDialogOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);

      if (!nextOpen) {
        setDialogSelectedObj(null);
        setDialogSelectedItems([]);
      }
    },
    [setOpen]
  );

  const handleDialogCancel = useCallback(() => {
    setDialogSelectedObj(null);
    setDialogSelectedItems([]);
    closeDialog();
  }, [closeDialog]);

  const handleDialogDoubleClick = useCallback(
    (row: T) => {
      if (supportsDialogMultiSelect) {
        return;
      }

      setDialogSelectedObj(row);

      if (multiple) {
        multipleSearch.appendSelection(row);
      } else {
        commitSelection(row);
      }

      closeDialog();
    },
    [closeDialog, commitSelection, multiple, multipleSearch, supportsDialogMultiSelect]
  );

  const handleDialogConfirm = useCallback(() => {
    if (multiple) {
      if (supportsDialogMultiSelect) {
        multipleSearch.replaceSelection(dialogSelectedItems);
      } else {
        multipleSearch.appendSelection(dialogSelectedObj);
      }
    } else {
      commitSelection(dialogSelectedObj ?? null);
    }

    setDialogSelectedObj(null);
    setDialogSelectedItems([]);
    closeDialog();
  }, [
    closeDialog,
    commitSelection,
    dialogSelectedItems,
    dialogSelectedObj,
    multiple,
    multipleSearch,
    supportsDialogMultiSelect
  ]);

  const relatedDisplayValue = useMemo(() => {
    if (relatedFieldValue) {
      return relatedFieldValue;
    }

    if (!displayRelatedField || !selectedObj) {
      return '';
    }

    return getDisplayText(selectedObj, displayRelatedField as string);
  }, [displayRelatedField, relatedFieldValue, selectedObj]);

  const disabledValue = multiple ? multipleSearch.inputValue : inputValue;
  const hasRelatedField = Boolean(displayRelatedField);
  const relatedFieldStyle: React.CSSProperties = {
    minWidth: 0,
    width: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  };
  const relatedFieldContainerStyle: React.CSSProperties = {
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden'
  };

  if (disabled) {
    return withErrorTooltip(
      <div className='flex w-full min-w-0 flex-col'>
        <div
          data-search-field-type='lookup'
          data-search-field-mode={multiple ? 'multiple' : 'single'}
          className={cn(disabledValueClassName, invalid && 'border-b border-b-red-500', className)}
          title={disabledValue}
        >
          {disabledValue}
        </div>
        {shouldRenderErrorMessage && <span className='mt-1 block text-xs text-red-500'>{fieldError}</span>}
      </div>
    );
  }

  if (multiple) {
    return withErrorTooltip(
      <div className='flex w-full min-w-0 flex-col'>
        <div
          ref={suggestionContainerRef}
          data-search-field-type='lookup'
          data-search-field-mode='multiple'
          className={cn('relative flex w-full min-w-0 flex-col', className)}
        >
          <SearchInputControl
            type={type}
            disabled={multipleSearch.isSearching}
            invalid={invalid}
            value={multipleSearch.inputValue}
            onChange={nextValue => {
              multipleSearch.handleInputChange(nextValue);
              handleInputChange(nextValue);
            }}
            onBlur={() => {
              handleInputBlur();
              triggerValidation();
              window.setTimeout(() => {
                void multipleSearch.handleSearch();
              }, 150);
            }}
            onKeyDown={event => {
              handleInputKeyDown(event);

              if (event.defaultPrevented) {
                return;
              }

              if (event.key === 'Enter') {
                event.preventDefault();
                void multipleSearch.handleSearch();
              }
            }}
            onFocus={handleInputFocus}
            placeholder={placeholder}
            loading={multipleSearch.isSearching}
            onOpenDialog={() => void openSearchDialog()}
          />

          <SearchSuggestionsDropdown
            shouldShow={shouldShowDropdown}
            dropdownRef={suggestionDropdownRef}
            dropdownStyle={dropdownPositionStyle}
            isLoading={isLoading}
            suggestions={suggestions}
            suggestionColumns={
              suggestionColumns.length > 0 ? suggestionColumns : [{ field: String(columnDisplay || '') }]
            }
            highlightedIndex={highlightedIndex}
            onHighlightIndexChange={setHighlightedIndex}
            onCommitSelection={handleMultipleSuggestionSelection}
            getColumnDisplayValue={getColumnDisplayValue}
            getColumnStyle={getColumnStyle}
            getItemKey={(row, index) => resolveRowId(row) || String(index)}
            showPagination={showPagination}
            canGoPrevious={canGoPrevious}
            canGoNext={canGoNext}
            onPreviousPage={goToPreviousPage}
            onNextPage={goToNextPage}
            showNoResults={showNoResults}
          />

          <SearchDialog
            checkboxSelection={supportsDialogMultiSelect}
            dialogBaseZIndex={dialogBaseZIndex}
            dialogActions={resolvedDialogActions}
            open={open}
            onOpenChange={handleDialogOpenChange}
            dialogTitle={dialogTitle}
            dialogPageSize={dialogPageSize}
            rows={rows}
            searchColumns={searchColumns}
            searchRequest={searchRequest}
            headerFields={headerFields}
            hideRowNumberColumn={hideRowNumberColumn}
            refreshKey={resolvedRefreshKey}
            selectedRowId={dialogSelectedObj ? resolveRowId(dialogSelectedObj) : null}
            selectedRows={dialogSelectedItems}
            onRowSelection={setDialogSelectedObj}
            onSelectionRowsChange={setDialogSelectedItems}
            onRowDoubleClick={handleDialogDoubleClick}
            onConfirm={handleDialogConfirm}
            onCancel={handleDialogCancel}
            onCloseDialog={handleDialogCancel}
            isConfirmDisabled={supportsDialogMultiSelect ? dialogSelectedItems.length === 0 : !dialogSelectedObj}
            isFullScreen={isFullScreen}
            onToggleFullScreen={isMobile ? undefined : toggleFullScreen}
          />
        </div>
        {shouldRenderErrorMessage && <span className='mt-1 block text-xs text-red-500'>{fieldError}</span>}
      </div>
    );
  }

  return withErrorTooltip(
    <div className='flex w-full min-w-0 flex-col'>
      <div
        data-search-field-type='lookup'
        data-search-field-mode='single'
        className={cn(
          'relative flex w-full min-w-0 max-w-full items-center',
          hasRelatedField && 'flex-1 overflow-hidden',
          className
        )}
        style={hasRelatedField ? relatedFieldContainerStyle : undefined}
      >
        <div
          ref={suggestionContainerRef}
          className={cn(
            'relative min-w-0',
            hasRelatedField ? 'max-w-[45%] shrink-0 basis-[clamp(7rem,35%,12rem)]' : 'w-full'
          )}
        >
          <SearchInputControl
            type={type}
            invalid={invalid}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={() => {
              handleInputBlur();
              triggerValidation();
            }}
            onKeyDown={event => {
              const key = event.key.toLowerCase();

              if ((event.ctrlKey || event.metaKey) && key === 'a') {
                event.stopPropagation();
                return;
              }

              if (event.key === ' ') {
                event.stopPropagation();
              }

              handleInputKeyDown(event);
            }}
            placeholder={placeholder}
            onOpenDialog={() => void openSearchDialog()}
          />
        </div>

        <SearchSuggestionsDropdown
          shouldShow={shouldShowDropdown}
          dropdownRef={suggestionDropdownRef}
          dropdownStyle={dropdownPositionStyle}
          isLoading={isLoading}
          suggestions={suggestions}
          suggestionColumns={
            suggestionColumns.length > 0 ? suggestionColumns : [{ field: String(columnDisplay || '') }]
          }
          highlightedIndex={highlightedIndex}
          onHighlightIndexChange={setHighlightedIndex}
          onCommitSelection={commitSelection}
          getColumnDisplayValue={getColumnDisplayValue}
          getColumnStyle={getColumnStyle}
          getItemKey={(row, index) => resolveRowId(row) || String(index)}
          showPagination={showPagination}
          canGoPrevious={canGoPrevious}
          canGoNext={canGoNext}
          onPreviousPage={goToPreviousPage}
          onNextPage={goToNextPage}
          showNoResults={showNoResults}
        />

        {displayRelatedField && (
          <div
            className={cn(
              'ml-0 mt-1 w-0 min-w-0 flex-1 truncate text-left text-sm text-gray-700 sm:ml-3 sm:mt-0',
              classNameRelatedField
            )}
            style={relatedFieldStyle}
            title={relatedDisplayValue}
          >
            {relatedDisplayValue}
          </div>
        )}

        <SearchDialog
          checkboxSelection={false}
          dialogBaseZIndex={dialogBaseZIndex}
          dialogActions={resolvedDialogActions}
          open={open}
          onOpenChange={handleDialogOpenChange}
          dialogTitle={dialogTitle}
          dialogPageSize={dialogPageSize}
          rows={rows}
          searchColumns={searchColumns}
          searchRequest={searchRequest}
          headerFields={headerFields}
          hideRowNumberColumn={hideRowNumberColumn}
          refreshKey={resolvedRefreshKey}
          selectedRowId={dialogSelectedObj ? resolveRowId(dialogSelectedObj) : null}
          onRowSelection={setDialogSelectedObj}
          onRowDoubleClick={handleDialogDoubleClick}
          onConfirm={handleDialogConfirm}
          onCancel={handleDialogCancel}
          onCloseDialog={handleDialogCancel}
          isConfirmDisabled={!dialogSelectedObj}
          isFullScreen={isFullScreen}
          onToggleFullScreen={isMobile ? undefined : toggleFullScreen}
        />
      </div>
      {shouldRenderErrorMessage && <span className='mt-1 block text-xs text-red-500'>{fieldError}</span>}
    </div>
  );
};

export const SearchFieldV2 = forwardRef(SearchFieldInner) as <T = any>(
  props: SearchFieldProps<T> & { ref?: React.ForwardedRef<SearchFieldRef<T>> }
) => React.ReactElement;
